/**
 * Web Push Hook for Browser/OS Notifications
 * 
 * WHEN TO USE:
 * - User has CLOSED the app/tab
 * - Shows native browser/OS notifications
 * - Requires user permission + Service Worker
 * 
 * HOW IT WORKS:
 * 1. Request notification permission from user
 * 2. Register Service Worker
 * 3. Create push subscription using VAPID public key
 * 4. Send subscription to backend
 * 5. Backend can now send push notifications even when tab is closed
 */

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '@/lib/config';

interface UseWebPushReturn {
    isSupported: boolean;
    isPermissionGranted: boolean;
    isSubscribed: boolean;
    isAvailable: boolean;
    requestPermission: () => Promise<boolean>;
    subscribe: () => Promise<boolean>;
    unsubscribe: () => Promise<boolean>;
    error: string | null;
}

/**
 * Base64 URL encoding helper
 * VAPID keys are base64-url encoded, need to convert for PushManager
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * useWebPush Hook
 * 
 * Manages Web Push subscription for browser/OS notifications.
 * Handles permission requests, Service Worker registration, and subscription.
 */
export const useWebPush = (): UseWebPushReturn => {
    const { user, authToken } = useAuth();
    const [isSupported, setIsSupported] = useState(false);
    const [isPermissionGranted, setIsPermissionGranted] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isAvailable, setIsAvailable] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null);

    // Check browser support
    useEffect(() => {
        const supported =
            'serviceWorker' in navigator &&
            'PushManager' in window &&
            'Notification' in window;

        setIsSupported(supported);

        if (!supported) {
            console.warn('Web Push not supported in this browser');
        }
    }, []);

    // Check permission status
    useEffect(() => {
        if (!isSupported) return;

        const checkPermission = () => {
            setIsPermissionGranted(Notification.permission === 'granted');
        };

        checkPermission();

        // Listen for permission changes (if browser supports it)
        if ('permissions' in navigator) {
            navigator.permissions.query({ name: 'notifications' as PermissionName })
                .then(permissionStatus => {
                    permissionStatus.addEventListener('change', checkPermission);
                    return () => permissionStatus.removeEventListener('change', checkPermission);
                })
                .catch(() => {
                    // Permissions API not fully supported, that's ok
                });
        }
    }, [isSupported]);

    // Fetch VAPID public key
    useEffect(() => {
        const fetchVapidKey = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/notifications/system-info`);

                if (response.data.web_push?.available) {
                    setVapidPublicKey(response.data.web_push.vapid_public_key);
                    setIsAvailable(true);
                } else {
                    console.warn('Web Push not configured on backend');
                    setIsAvailable(false);
                }
            } catch (err) {
                console.error('Failed to fetch VAPID public key:', err);
                setError('Failed to load push notification config');
            }
        };

        if (isSupported) {
            fetchVapidKey();
        }
    }, [isSupported]);

    /**
     * Request notification permission from user
     * Returns true if granted, false otherwise
     */
    const requestPermission = useCallback(async (): Promise<boolean> => {
        if (!isSupported) {
            setError('Notifications not supported');
            return false;
        }

        try {
            const permission = await Notification.requestPermission();
            const granted = permission === 'granted';
            setIsPermissionGranted(granted);

            if (!granted) {
                setError('Notification permission denied');
            }

            return granted;
        } catch (err) {
            console.error('Error requesting permission:', err);
            setError('Failed to request permission');
            return false;
        }
    }, [isSupported]);

    /**
     * Subscribe to push notifications
     * 
     * Steps:
     * 1. Register Service Worker
     * 2. Get push subscription from PushManager
     * 3. Send subscription to backend
     */
    const subscribe = useCallback(async (): Promise<boolean> => {
        if (!isSupported || !isAvailable || !vapidPublicKey) {
            setError('Push notifications not available');
            return false;
        }

        if (!isPermissionGranted) {
            const granted = await requestPermission();
            if (!granted) return false;
        }

        if (!user || !authToken) {
            setError('User not authenticated');
            return false;
        }

        try {
            // 1. Register Service Worker
            const registration = await navigator.serviceWorker.register('/service-worker.js');

            // Wait for Service Worker to be ready
            await navigator.serviceWorker.ready;

            // 2. Create push subscription
            const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: convertedVapidKey
            });

            // 3. Send subscription to backend
            const subData = subscription.toJSON();

            await axios.post(
                `${API_BASE_URL}/notifications/push-subscriptions`,
                {
                    subscription: subData,
                    user_agent: navigator.userAgent
                },
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`
                    }
                }
            );

            setIsSubscribed(true);
            setError(null);
            return true;

        } catch (err: any) {
            console.error('❌ Error subscribing to push:', err);

            // Helpful error messages for common push issues
            if (err.name === 'NotAllowedError') {
                setError('Notification permission was denied by the browser.');
            } else if (err.name === 'InvalidCharacterError') {
                setError('The VAPID public key is invalid.');
            } else {
                setError(err.message || 'Failed to subscribe to browser notifications.');
            }

            return false;
        }
    }, [isSupported, isAvailable, isPermissionGranted, vapidPublicKey, user, authToken, requestPermission]);

    /**
     * Unsubscribe from push notifications
     */
    const unsubscribe = useCallback(async (): Promise<boolean> => {
        if (!isSupported || !user || !authToken) {
            return false;
        }

        try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (!registration) return false;

            const subscription = await registration.pushManager.getSubscription();
            if (!subscription) return false;

            // Get endpoint before unsubscribing
            const endpoint = subscription.endpoint;

            // Unsubscribe from browser
            await subscription.unsubscribe();

            // Remove from backend
            await axios.delete(
                `${API_BASE_URL}/notifications/push-subscriptions`,
                {
                    data: { endpoint },
                    headers: {
                        Authorization: `Bearer ${authToken}`
                    }
                }
            );

            setIsSubscribed(false);
            return true;

        } catch (err: any) {
            console.error('Error unsubscribing:', err);
            setError(err.message || 'Failed to unsubscribe');
            return false;
        }
    }, [isSupported, user, authToken]);

    // Check existing subscription on mount
    useEffect(() => {
        const checkExistingSubscription = async () => {
            if (!isSupported || !isPermissionGranted) return;

            try {
                const registration = await navigator.serviceWorker.getRegistration();
                if (!registration) return;

                const subscription = await registration.pushManager.getSubscription();
                setIsSubscribed(!!subscription);
            } catch (err) {
                console.error('Error checking subscription:', err);
            }
        };

        checkExistingSubscription();
    }, [isSupported, isPermissionGranted]);

    return {
        isSupported,
        isPermissionGranted,
        isSubscribed,
        isAvailable,
        requestPermission,
        subscribe,
        unsubscribe,
        error
    };
};
